export const GA_MEASUREMENT_ID = 'G-755Q5SLSNY';
export const ANALYTICS_CONSENT_KEY = 'cookie_analytics';
export const CONSENT_VERSION_KEY = 'cookie_consent_version';
export const CURRENT_CONSENT_VERSION = '2026-09-18-ga4';
export const CONSENT_SETTINGS_EVENT = 'open-cookie-settings';

declare global {
  interface Window {
    dataLayer?: IArguments[];
    gtag?: (...args: unknown[]) => void;
  }
}

const GOOGLE_TAG_SCRIPT_ID = 'google-analytics-tag';

export const hasAnalyticsConsent = () =>
  localStorage.getItem(ANALYTICS_CONSENT_KEY) === 'true' &&
  localStorage.getItem(CONSENT_VERSION_KEY) === CURRENT_CONSENT_VERSION;

const ensureGtag = () => {
  window.dataLayer = window.dataLayer || [];
  // Google Tag consumes Arguments objects, not plain arrays.
  window.gtag = window.gtag || function (..._args: unknown[]) {
    window.dataLayer?.push(arguments);
  };
};

export const initializeAnalytics = () => {
  if (!hasAnalyticsConsent()) return false;

  ensureGtag();

  if (!document.getElementById(GOOGLE_TAG_SCRIPT_ID)) {
    window.gtag?.('consent', 'default', {
      analytics_storage: 'granted',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
    window.gtag?.('js', new Date());
    window.gtag?.('config', GA_MEASUREMENT_ID, {
      send_page_view: false,
      anonymize_ip: true,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });

    const script = document.createElement('script');
    script.id = GOOGLE_TAG_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);
  }

  return true;
};

const removeGoogleAnalyticsCookies = () => {
  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0]?.trim();
    if (!name || (!name.startsWith('_ga') && name !== '_gid' && name !== '_gat')) return;
    document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
    document.cookie = `${name}=; Max-Age=0; path=/; domain=.aysegulsarac.com; SameSite=Lax`;
  });
};

export const setAnalyticsConsent = (enabled: boolean) => {
  localStorage.setItem(ANALYTICS_CONSENT_KEY, String(enabled));
  localStorage.setItem(CONSENT_VERSION_KEY, CURRENT_CONSENT_VERSION);

  if (enabled) {
    initializeAnalytics();
    window.gtag?.('consent', 'update', { analytics_storage: 'granted' });
    return;
  }

  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
  }
  removeGoogleAnalyticsCookies();
};

export const trackPageView = () => {
  if (!hasAnalyticsConsent() || !initializeAnalytics()) return;
  window.gtag?.('event', 'page_view', {
    page_title: document.title,
    page_location: window.location.href,
    page_path: window.location.pathname + window.location.search,
  });
};

export const trackEvent = (eventName: string, parameters: Record<string, string> = {}) => {
  if (!hasAnalyticsConsent() || !initializeAnalytics()) return;
  window.gtag?.('event', eventName, parameters);
};
